import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="password-validation"
export default class extends Controller {
  static targets = [
    "password",
    "confirmation",
    "submit",
    "error",
    "passwordFeedback",
    "confirmationFeedback",
    "email",
    "lengthRequirement",
    "uppercaseRequirement",
    "lowercaseRequirement",
    "numberRequirement",
    "confirmationRequirement",
    "validationErrors"
  ];

  connect() {
    // Disable submit button initially
    this.disableSubmitButton();

    // Set up validation listeners
    this.validateOnInput();

    // Initial validation of all fields
    this.validateAll();

    // Check if Turnstile is already completed (in case of page refresh)
    setTimeout(() => this.checkTurnstileStatus(), 500);

    // Show validation errors if password field is not empty
    if (this.passwordTarget.value !== "") {
      this.showValidationErrors();
    }
  }

  validateOnInput() {
    this.passwordTarget.addEventListener("input", () => {
      this.validatePassword();
      this.showValidationErrors();
    });
    this.confirmationTarget.addEventListener("input", () => {
      this.validateConfirmation();
      this.updateConfirmationRequirement();
    });
    this.emailTarget.addEventListener("input", () => this.validateEmail());
  }

  validateAll() {
    this.validatePassword();
    this.validateConfirmation();
    this.validateEmail();
    this.updateSubmitButton();
  }

  validatePassword() {
    const password = this.passwordTarget.value;
    const errors = [];
    let isValid = true;

    // Check minimum length (12 characters)
    const lengthValid = password.length >= 12;
    if (!lengthValid) {
      errors.push("Password must be at least 12 characters long");
      isValid = false;
    }
    this.updateRequirement(this.lengthRequirementTarget, lengthValid);

    // Check for at least one uppercase letter
    const uppercaseValid = /[A-Z]/.test(password);
    if (!uppercaseValid) {
      errors.push("Password must contain at least one uppercase letter");
      isValid = false;
    }
    this.updateRequirement(this.uppercaseRequirementTarget, uppercaseValid);

    // Check for at least one lowercase letter
    const lowercaseValid = /[a-z]/.test(password);
    if (!lowercaseValid) {
      errors.push("Password must contain at least one lowercase letter");
      isValid = false;
    }
    this.updateRequirement(this.lowercaseRequirementTarget, lowercaseValid);

    // Check for at least one number
    const numberValid = /[0-9]/.test(password);
    if (!numberValid) {
      errors.push("Password must contain at least one number");
      isValid = false;
    }
    this.updateRequirement(this.numberRequirementTarget, numberValid);

    // Update feedback
    this.updatePasswordFeedback(isValid, errors);

    // Also validate confirmation if it has a value
    if (this.confirmationTarget.value) {
      this.validateConfirmation();
    }

    this.updateSubmitButton();
  }

  showValidationErrors() {
    // Show validation errors if password field is not empty
    if (this.passwordTarget.value !== "") {
      this.validationErrorsTarget.classList.remove("hidden");
    } else {
      this.validationErrorsTarget.classList.add("hidden");
    }
  }

  updateRequirement(element, isValid) {
    if (this.passwordTarget.value === "") {
      // Reset to default state if password is empty
      element.classList.remove("text-green-600");
      element.classList.add("text-red-600");
    } else if (isValid) {
      element.classList.remove("text-red-600");
      element.classList.add("text-green-600");
    } else {
      element.classList.remove("text-green-600");
      element.classList.add("text-red-600");
    }
  }

  validateConfirmation() {
    const password = this.passwordTarget.value;
    const confirmation = this.confirmationTarget.value;
    const isValid = password === confirmation && confirmation !== "";

    // Update feedback
    this.updateConfirmationFeedback(isValid);
    this.updateConfirmationRequirement();
    this.updateSubmitButton();
  }

  updateConfirmationRequirement() {
    const password = this.passwordTarget.value;
    const confirmation = this.confirmationTarget.value;

    // Only show mismatch error if both fields have values and they don't match
    if (password !== "" && confirmation !== "" && password !== confirmation) {
      this.confirmationRequirementTarget.classList.remove("hidden");
    } else {
      this.confirmationRequirementTarget.classList.add("hidden");
    }
  }

  validateEmail() {
    const email = this.emailTarget.value;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const isValid = emailRegex.test(email);

    // Update UI
    if (!isValid && email !== "") {
      this.emailTarget.classList.add("border-red-500");
      this.emailTarget.classList.remove("border-green-500");
    } else if (isValid && email !== "") {
      this.emailTarget.classList.add("border-green-500");
      this.emailTarget.classList.remove("border-red-500");
    } else {
      this.emailTarget.classList.remove("border-red-500", "border-green-500");
    }

    this.updateSubmitButton();
  }

  updatePasswordFeedback(isValid, errors) {
    // Only update the border color, don't show individual messages
    if (!isValid) {
      this.passwordTarget.classList.add("border-red-500");
      this.passwordTarget.classList.remove("border-green-500");
    } else {
      this.passwordTarget.classList.add("border-green-500");
      this.passwordTarget.classList.remove("border-red-500");
    }

    // Store the validation state for the submit button check
    this.passwordFeedbackTarget.classList.remove("text-green-600", "text-red-600");
    if (isValid) {
      this.passwordFeedbackTarget.classList.add("text-green-600");
    } else {
      this.passwordFeedbackTarget.classList.add("text-red-600");
    }

    // Keep the feedback element hidden
    this.passwordFeedbackTarget.classList.add("hidden");
  }

  updateConfirmationFeedback(isValid) {
    // Only update the border color, don't show individual messages
    if (!isValid && this.confirmationTarget.value !== "") {
      this.confirmationTarget.classList.add("border-red-500");
      this.confirmationTarget.classList.remove("border-green-500");
    } else if (isValid && this.confirmationTarget.value !== "") {
      this.confirmationTarget.classList.add("border-green-500");
      this.confirmationTarget.classList.remove("border-red-500");
    } else {
      this.confirmationTarget.classList.remove("border-red-500", "border-green-500");
    }

    // Store the validation state for the submit button check
    this.confirmationFeedbackTarget.classList.remove("text-green-600", "text-red-600");
    if (isValid || this.confirmationTarget.value === "") {
      this.confirmationFeedbackTarget.classList.add("text-green-600");
    } else {
      this.confirmationFeedbackTarget.classList.add("text-red-600");
    }

    // Keep the feedback element hidden
    this.confirmationFeedbackTarget.classList.add("hidden");
  }

  updateSubmitButton() {
    // Check if all validations pass
    const passwordValid = this.isPasswordValid();
    const confirmationValid = this.isConfirmationValid();
    const emailValid = this.isEmailValid();
    const turnstileValid = this.isTurnstileValid();

    if (passwordValid && confirmationValid && emailValid && turnstileValid) {
      this.enableSubmitButton();
    } else {
      this.disableSubmitButton();
    }
  }

  isPasswordValid() {
    return this.areAllPasswordRequirementsMet();
  }

  isConfirmationValid() {
    const password = this.passwordTarget.value;
    const confirmation = this.confirmationTarget.value;

    // If confirmation is empty, it's valid (not filled in yet)
    if (confirmation === "") return true;

    // Otherwise, it must match the password
    return password === confirmation;
  }

  isEmailValid() {
    const email = this.emailTarget.value;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return email !== "" && emailRegex.test(email);
  }

  isTurnstileValid() {
    // Check if Turnstile response is present
    const turnstileResponse = document.querySelector('[name="cf-turnstile-response"]');
    return turnstileResponse && turnstileResponse.value;
  }

  enableSubmitButton() {
    this.submitTarget.disabled = false;
    this.submitTarget.classList.remove(
      "opacity-50",
      "cursor-not-allowed",
      "bg-gray-400",
      "hover:bg-gray-500"
    );
    this.submitTarget.classList.add("bg-orange-500", "hover:bg-orange-600");
  }

  disableSubmitButton() {
    this.submitTarget.disabled = true;
    this.submitTarget.classList.add(
      "opacity-50",
      "cursor-not-allowed",
      "bg-gray-400",
      "hover:bg-gray-500"
    );
    this.submitTarget.classList.remove("bg-orange-500", "hover:bg-orange-600");
  }

  beforeSubmit(event) {
    // Validate all fields before submission
    this.validateAll();

    // Show validation errors
    this.showValidationErrors();
    this.updateConfirmationRequirement();

    // Check if all validations pass
    const passwordValid = this.isPasswordValid();
    const confirmationValid = this.isConfirmationValid();
    const emailValid = this.isEmailValid();
    const turnstileValid = this.isTurnstileValid();

    if (!passwordValid || !confirmationValid || !emailValid || !turnstileValid) {
      event.preventDefault();

      // Show error in the form error section if it exists
      if (this.hasErrorTarget) {
        let errorMessages = [];

        if (!emailValid) {
          errorMessages.push("Please enter a valid email address");
        }

        if (!passwordValid) {
          const passwordErrors = this.getPasswordErrors();
          errorMessages = errorMessages.concat(passwordErrors);
        }

        if (!confirmationValid && this.confirmationTarget.value !== "") {
          errorMessages.push("Passwords do not match");
        }

        if (!turnstileValid) {
          errorMessages.push("Please complete the Turnstile challenge");
        }

        // Create error HTML
        const errorHTML = `
          <div class="flex">
            <div class="flex-shrink-0">
              <i class="fas fa-exclamation-circle text-red-400"></i>
            </div>
            <div class="ml-3">
              <h3 class="text-sm font-medium text-red-800">
                Please fix the following errors:
              </h3>
              <div class="mt-2 text-sm text-red-700">
                <ul class="list-disc pl-5 space-y-1">
                  ${errorMessages.map((msg) => `<li>${msg}</li>`).join("")}
                </ul>
              </div>
            </div>
          </div>
        `;

        this.errorTarget.innerHTML = errorHTML;
        this.errorTarget.classList.remove("hidden");
      }
    }
  }

  getPasswordErrors() {
    const password = this.passwordTarget.value;
    const errors = [];

    if (password === "") {
      errors.push("Password is required");
      return errors;
    }

    // Check minimum length (12 characters)
    if (password.length < 12) {
      errors.push("Password must be at least 12 characters long");
    }

    // Check for at least one uppercase letter
    if (!/[A-Z]/.test(password)) {
      errors.push("Password must contain at least one uppercase letter");
    }

    // Check for at least one lowercase letter
    if (!/[a-z]/.test(password)) {
      errors.push("Password must contain at least one lowercase letter");
    }

    // Check for at least one number
    if (!/[0-9]/.test(password)) {
      errors.push("Password must contain at least one number");
    }

    return errors;
  }

  checkTurnstileStatus() {
    const turnstileResponse = document.querySelector('[name="cf-turnstile-response"]');
    if (turnstileResponse && turnstileResponse.value) {
      this.updateSubmitButton();
    }
  }

  // Add a method to check if all password requirements are met
  areAllPasswordRequirementsMet() {
    const password = this.passwordTarget.value;
    if (password === "") return false;

    return (
      password.length >= 12 &&
      /[A-Z]/.test(password) &&
      /[a-z]/.test(password) &&
      /[0-9]/.test(password)
    );
  }
}
